import { createContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import InactivityWarning from '../components/InactivityWarning/InactivityWarning';

const SessionExpiringContext = createContext();

// Configurable constants
const TIMEOUT_DURATION =
    (parseInt(process.env.REACT_APP_CLIENT_CONSOLE_SESSION_TIMEOUT) || 15) * 60; // Session timeout in seconds
const WARNING_DURATION = 60; // Show warning 60 seconds before timeout

// Debugging aides
if (!window.SEC) {
    window.SEC = { verboseDebug: false };
}
const debugConsoleLog = (s) => {
    if (window.SEC.verboseDebug)
        console.log(`${new Date().toLocaleTimeString()} SEC_${s}`);
};

export const SessionExpiringProvider = ({ children }) => {
    const { isAuthenticated, logout } = useAuth0();

    // There are really only two pieces to this logic-
    // (1) The activity flagger- this responds to any input and all it does is set a flag
    //     indicating we should keep the session alive at the next tic
    // (2) The time tracker- this is an interval that operates every second and
    //     determines when to show the warning and when to log out

    // Since the logout action effectively full refreshes we thankfully dont
    // have to worry about or manage any resets/cleanup

    // Activity Flagger
    const keepAliveRef = useRef();
    const [eventListenersAreBound, setEventListenersAroundBound] = useState(false);
    useEffect(() => {
        if (!isAuthenticated || eventListenersAreBound) return;
        debugConsoleLog('AF Initializing');

        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll'];

        const handleActivity = (e) => {
            debugConsoleLog('AF activity detected: ' + e);
            keepAliveRef.current = true;
        };

        activityEvents.forEach((event) => {
            document.addEventListener(event, handleActivity, true);
        });

        setEventListenersAroundBound(true);
    }, [isAuthenticated, eventListenersAreBound]);

    // Time Tracker
    const [showWarning, setShowWarning] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(60);
    const [intervalId, setIntervalId] = useState();
    const lastActivityTimeRef = useRef();
    useEffect(() => {
        // Ensure if we've logged out the interval has been cleared
        if (!isAuthenticated && intervalId) {
            debugConsoleLog(`TT Guard- Clearing interval: ${intervalId}`);
            clearInterval(intervalId);
            setIntervalId(null);
            return;
        }

        // Early return for dependencies / requirements not being setup yet
        if (!isAuthenticated || !eventListenersAreBound || !logout) {
            debugConsoleLog(
                `TT Guard- Requirements/dependencies not met. isAuthenticated: ${isAuthenticated}; eventListenersAreBound: ${eventListenersAreBound}; logout: ${typeof logout}`
            );
            return;
        }

        // Early return for the interval already running
        if (intervalId) {
            debugConsoleLog(`TT Guard- Interval already created: ${intervalId}`);
            return;
        }

        lastActivityTimeRef.current = Math.floor(Date.now() / 1000);

        const interval = setInterval(() => {
            const currentTime = Math.floor(Date.now() / 1000);
            if (keepAliveRef.current) {
                debugConsoleLog(`TT tic - keepAlive true - lastActivityTimeRef updating to: ${currentTime}`);
                lastActivityTimeRef.current = currentTime;
                setShowWarning(false);
                keepAliveRef.current = false;
            } else {
                const logOutTime = lastActivityTimeRef.current + TIMEOUT_DURATION;
                const displayWarningTime = logOutTime - WARNING_DURATION;
                debugConsoleLog('TT tic - keepAlive false');
                debugConsoleLog(
                    `currentTime: ${currentTime}; WarningIn: ${
                        displayWarningTime - currentTime
                    }; LogoutIn: ${logOutTime - currentTime}`
                );

                if (currentTime >= displayWarningTime) {
                    debugConsoleLog('TT Warning time triggered.');
                    setShowWarning(true);
                    const remaining = logOutTime - currentTime;
                    setSecondsRemaining(Math.max(remaining, 0));
                }
                
                if (currentTime >= logOutTime) {
                    debugConsoleLog('TT LogOut time triggered.');
                    clearInterval(interval);
                    logout({ logoutParams: { returnTo: window.location.origin } });
                }
            }
        }, 1000);
        setIntervalId(interval);
    }, [isAuthenticated, eventListenersAreBound, logout, intervalId]);

    // A perk to this being in a Context is if needed in the future we can expose some
    // triggerActivity() function here to allow downstream code to keep the session alive

    return (
        <SessionExpiringContext.Provider value={{}}>
            {children}
            <InactivityWarning open={showWarning} secondsRemaining={secondsRemaining} />
        </SessionExpiringContext.Provider>
    );
};
